// SPDX-License-Identifier: MIT
// "↪ Continue dans ton terminal" — the web→terminal handoff for an OPEN project
// (layout B, State C). The full first-time wizard lives in forgejo-next-steps
// (shown right after bootstrap) ; this is the slim, always-visible reminder when
// a user RE-OPENS a project: clone it + launch Claude, and from there inference
// runs on THEIR subscription (the web "gift" key only paid for the magnify).
// Only rendered when the project has been pushed (has a repo URL).
import { t } from '../i18n/t';
import { escapeHtml } from '../lib/escape';
import { deriveForgejoUrls, buildAuthedCloneUrl } from '../projects/forgejo';

export interface TerminalHandoffOptions {
  repoUrl: string; // forgejo html_url of the pushed project
  // Read-only token so the HTTPS clone authenticates against the login-walled
  // instance — automatic for ALL users, no SSH key / no per-user setup. When
  // absent, falls back to the plain clone URL (anonymous, will 303 on a fully
  // login-walled instance — kept only as a graceful degrade).
  cloneToken?: string | null;
}

export function renderTerminalHandoff(opts: TerminalHandoffOptions): HTMLElement {
  // The instance Forgejo is login-walled (REQUIRE_SIGNIN_VIEW) → an anonymous
  // clone 303s to /user/login ("Échec du gitclone"). SSH would need a per-user
  // key (not automatic for all users). So we embed a READ-ONLY token as HTTPS
  // basic-auth → works for everyone, zero setup. cf gitclone volet ②.
  const cloneUrl = buildAuthedCloneUrl(opts.repoUrl, opts.cloneToken);
  const repoName =
    deriveForgejoUrls(opts.repoUrl).repo_name ||
    (opts.repoUrl.replace(/\.git$/, '').split('/').filter(Boolean).pop() ?? 'mon-projet');
  const cmd = `git clone ${cloneUrl} && cd ${repoName} && claude`;

  const root = document.createElement('section');
  root.className = 'terminal-handoff';
  root.setAttribute('data-component', 'terminal-handoff');
  root.innerHTML = `
    <h3 class="terminal-handoff__heading">${escapeHtml(t('handoff.heading'))}</h3>
    <p class="terminal-handoff__sub">${escapeHtml(t('handoff.sub'))}</p>
    <div class="carnet-cmd">
      <span class="carnet-cmd__prompt" aria-hidden="true">$</span>
      <code data-component="handoff-cmd">${escapeHtml(cmd)}</code>
      <button type="button" data-component="handoff-copy" class="carnet-cmd__copy" aria-label="${escapeHtml(t('connectClaude.copyAria'))}">📋</button>
    </div>
    <p class="terminal-handoff__hint">${escapeHtml(t('handoff.authHint'))}</p>
    <p class="terminal-handoff__byoai">${escapeHtml(t('handoff.byoai'))}</p>
  `;

  const btn = root.querySelector<HTMLButtonElement>('[data-component="handoff-copy"]')!;
  const code = root.querySelector<HTMLElement>('[data-component="handoff-cmd"]')!;
  btn.addEventListener('click', async () => {
    let ok = false;
    try {
      if (navigator.clipboard?.writeText) { await navigator.clipboard.writeText(code.textContent ?? ''); ok = true; }
    } catch { /* ignore */ }
    const orig = btn.textContent ?? '📋';
    btn.textContent = ok ? '✓' : '✗';
    setTimeout(() => { btn.textContent = orig; }, 1600);
  });

  return root;
}
